import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Box, IconButton, Popover, Typography } from "@mui/material";
import { Send } from "@mui/icons-material";
import { useUsers } from "../../hooks/useUsers";
import { useAgents } from "../../hooks/useAgents";
import MicRecorder from "mic-recorder-to-mp3";
import ChannelSelector from "./ChannelSelector";
import MessageInputField from "./MessageInputField";
import FileDropHandler from "./FileDropHandler";
import FilePreview from "./FilePreview";
import AudioPreview from "./AudioPreview";
import RecordingIndicator from "./RecordingIndicator";
import EmojiPicker from "./EmojiPicker";
import { useMediaQuery } from "@mui/material";
import { GetChannelTypeById, GetChannelById } from "../../utils/utils";
import MessagePhoneChannel from "./MessagePhoneChannel";
import MessageWhatsAppChannel from "./MessageWhatsAppChannel";
import MessageEmailChannel from "./MessageEmailChannel";
import MessageMessengerChannel from "./MessageMessengerChannel";
import MessageSMSChannel from "./MessageSMSChannel";
import MessageAppChannel from "./MessageAppChannel";
import MessageWordpressChannel from "./MessageWordpressChannel";
import { generateSuggestedReply, getConversationMessageSuggestions, fetchData } from "../../api/client";
import { isWhatsAppQrChannelTipo } from "../../constants/channelCatalog";

const formatOptions = {
  WhatsApp: [
    { icon: <b>B</b>, action: "**texto**", tooltip: "Negrito" },
    { icon: <i>I</i>, action: "_texto_", tooltip: "Itálico" },
    { icon: <u>U</u>, action: "__texto__", tooltip: "Sublinhado" },
  ],
  Email: [
    { icon: <b>B</b>, action: "<b>texto</b>", tooltip: "Negrito" },
    { icon: <i>I</i>, action: "<i>texto</i>", tooltip: "Itálico" },
    { icon: <u>U</u>, action: "<u>texto</u>", tooltip: "Sublinhado" },
  ],
};

const isChatBotUser = (u) => Number(u?.ura) === 1;

const MessageInput = ({ onSendMessage, selectedConversation, messages = [], hasHistory = false }) => {
  const [selectedChannel, setSelectedChannel] = useState(() => {
    // Recupera o valor do sessionStorage ou usa o valor inicial de selectedConversation
    return (
      sessionStorage.getItem(`selectedChannel${selectedConversation?.id}`) ||
      selectedConversation?.channelId
    );
  });
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [audioURL, setAudioURL] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mp3RecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const discardRequestedRef = useRef(false);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const fileInputRef = useRef(null);
  const [origin, setOrigin] = useState("computer");
  const [destination, setDestination] = useState("(21) 99876-5432");
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isGeneratingSuggestion, setIsGeneratingSuggestion] = useState(false);
  const [suggestions, setSuggestions] = useState({ templates: [], quickReplies: [] });
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState(null);
  const [transferTo, setTransferTo] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const mentionAnchorRef = useRef(null);
  const suggestionExtrasRef = useRef({ focusProductIds: [], focusLineItemIds: [] });
  const [mentionHighlightIndex, setMentionHighlightIndex] = useState(0);
  const [mentionOverlayPosition, setMentionOverlayPosition] = useState(null);

  useEffect(() => {
    const handler = (ev) => {
      const d = ev.detail || {};
      if (d.conversationId != null && Number(d.conversationId) !== Number(selectedConversation?.id)) {
        return;
      }
      if (d.appendText) {
        const t = String(d.appendText);
        setMessage((m) => (m && m.trim() ? `${m.trim()}\n\n${t}` : t));
      }
      if (d.imageUrl && /^https?:\/\//i.test(String(d.imageUrl))) {
        const url = String(d.imageUrl);
        (async () => {
          const blobToFile = (blob) => {
            const ext =
              (blob.type || "").includes("png") ? "png" : (blob.type || "").includes("webp") ? "webp" : "jpeg";
            return new File([blob], `imagem.${ext}`, { type: blob.type || "image/jpeg" });
          };
          try {
            let blob;
            try {
              const r = await fetch(url, { mode: "cors" });
              if (!r.ok) throw new Error(`HTTP ${r.status}`);
              blob = await r.blob();
            } catch {
              // Loja/CDN sem CORS: o backend já baixa e envia ao S3 (mesmo fluxo da importação de produtos).
              const resp = await fetchData("/uploads/fetch", "POST", {
                url,
                prefix: "chat-compose",
                filename: "imagem",
              });
              const proxied = resp?.data?.url;
              if (!proxied) throw new Error("proxy sem url");
              const r2 = await fetch(proxied, { mode: "cors" });
              if (!r2.ok) throw new Error(`S3 ${r2.status}`);
              blob = await r2.blob();
            }
            setFiles([blobToFile(blob)]);
            setAudioURL(null);
          } catch {
            // eslint-disable-next-line no-alert
            alert("Não foi possível baixar a imagem (CORS). O link foi colocado no texto.");
            setMessage((m) => (m && m.trim() ? `${m.trim()}\n${url}` : url));
          }
        })();
      }
      if (Array.isArray(d.focusProductIds) && d.focusProductIds.length) {
        suggestionExtrasRef.current.focusProductIds = d.focusProductIds
          .map((x) => Number(x))
          .filter((n) => n > 0);
      }
      if (Array.isArray(d.focusLineItemIds) && d.focusLineItemIds.length) {
        suggestionExtrasRef.current.focusLineItemIds = d.focusLineItemIds
          .map((x) => Number(x))
          .filter((n) => n > 0);
      }
    };
    window.addEventListener("xpro-chat-compose", handler);
    return () => window.removeEventListener("xpro-chat-compose", handler);
  }, [selectedConversation?.id]);

  // Precisa incluir ChatBots (ura=1) para o @/transferência permitir direcionar ao bot.
  const { data: usersResp } = useUsers(1, 150, "", "todos");
  const { data: agentsResp } = useAgents(1, 200, "");
  const users = usersResp?.users ?? [];
  const agents = agentsResp?.agents ?? [];
  const humanUsers = (users || []).filter((u) => !isChatBotUser(u));
  const chatbotUsers = (users || []).filter(isChatBotUser);

  const lastAt = message.lastIndexOf("@");
  const mentionQuery = lastAt >= 0 ? message.slice(lastAt + 1) : "";
  const mentionOpen = lastAt >= 0 && !/\s/.test(mentionQuery);
  const mentionQueryLower = (mentionQuery || "").toLowerCase();

  const mentionOptions = useMemo(() => {
    const human = humanUsers.map((u) => ({ type: "user", id: u.id, label: u.nome || u.name || "", kind: "Humano" }));
    const chatbots = chatbotUsers.map((u) => ({ type: "user", id: u.id, label: u.nome || u.name || "", kind: "ChatBot" }));
    const agentes = (agents || []).map((a) => ({ type: "agent", id: a.id, label: a.name || a.nome || "", kind: "Agente de IA" }));
    const all = [...human, ...chatbots, ...agentes].filter((o) => o.label);
    if (!mentionQueryLower) return all;
    return all.filter((o) => (o.label || "").toLowerCase().includes(mentionQueryLower));
  }, [humanUsers, chatbotUsers, agents, mentionQueryLower]);

  const handleSelectMention = (option) => {
    if (lastAt < 0) return;
    const newMessage = message.slice(0, lastAt + 1) + (option.label || "") + " ";
    setMessage(newMessage);
    setTransferTo({ type: option.type, id: option.id, label: option.label });
  };

  // Reset highlight when options or query change
  useEffect(() => {
    setMentionHighlightIndex(0);
  }, [mentionOptions.length, mentionQuery]);

  // Position overlay above the input (no focus trap)
  useLayoutEffect(() => {
    if (mentionOpen && mentionOptions.length > 0 && mentionAnchorRef.current) {
      const rect = mentionAnchorRef.current.getBoundingClientRect();
      const gap = 8;
      setMentionOverlayPosition({
        bottom: window.innerHeight - rect.top + gap,
        left: rect.left,
        width: Math.max(rect.width, 320),
      });
    } else {
      setMentionOverlayPosition(null);
    }
  }, [mentionOpen, mentionOptions.length]);

  // Keyboard: arrows, Enter, Escape while mention list is open (capture so input keeps focus)
  useEffect(() => {
    if (!mentionOpen || mentionOptions.length === 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (lastAt >= 0) setMessage((m) => m.slice(0, lastAt));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        const opt = mentionOptions[mentionHighlightIndex];
        if (opt) handleSelectMention(opt);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setMentionHighlightIndex((i) => (i + 1) % mentionOptions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setMentionHighlightIndex((i) => (i - 1 + mentionOptions.length) % mentionOptions.length);
        return;
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [mentionOpen, mentionOptions, mentionHighlightIndex, lastAt]);

  const channelType = GetChannelTypeById(selectedChannel);
  const currentChannel = GetChannelById(selectedChannel);
  const channelName = currentChannel?.nome || currentChannel?.name || null;
  const selectedChannelTipo =
    currentChannel?.tipo != null && currentChannel?.tipo !== "" ? Number(currentChannel.tipo) : null;

  useEffect(() => {
    const initialChannel =
      sessionStorage.getItem(`selectedChannel${selectedConversation?.id}`) ||
      selectedConversation?.channelId;

    setSelectedChannel(initialChannel);
  }, [selectedConversation?.id, selectedConversation?.channelId]);

  // Ao apagar o texto do input, desvincular o template selecionado (evita enviar template ao enviar mensagem vazia/editada).
  useEffect(() => {
    if (!message.trim()) {
      setSelectedTemplate(null);
    }
  }, [message]);

  // Ao apagar o @ ou o nome do usuário do input, desvincular transferTo para não transferir se o usuário foi removido do texto.
  useEffect(() => {
    if (!transferTo) return;
    const lastAt = message.lastIndexOf("@");
    if (lastAt < 0) {
      setTransferTo(null);
      return;
    }
    const afterAt = message.slice(lastAt + 1);
    const label = transferTo.label || "";
    const stillMentioned = afterAt === label || afterAt === label + " " || afterAt.startsWith(label + " ");
    if (!stillMentioned) {
      setTransferTo(null);
    }
  }, [message, transferTo]);

  // Carrega sugestões (templates + respostas pré-definidas) ao mudar conversa, canal de envio ou tipo da solicitação.
  useEffect(() => {
    let isCancelled = false;

    async function loadSuggestions() {
      if (!selectedConversation?.id) return;
      if (channelType !== "WhatsApp") {
        if (!isCancelled) {
          setSuggestions({ templates: [], quickReplies: [] });
          setIsLoadingSuggestions(false);
          setSuggestionsError(null);
        }
        return;
      }

      try {
        setIsLoadingSuggestions(true);
        setSuggestionsError(null);
        const params = {
          originId: selectedChannel ?? selectedConversation?.channelId ?? undefined,
          typeId: selectedConversation?.typeId ?? undefined,
        };
        const data = await getConversationMessageSuggestions(selectedConversation.id, params);
        if (isCancelled) return;
        setSuggestions({
          templates: Array.isArray(data?.templates) ? data.templates : [],
          quickReplies: Array.isArray(data?.quickReplies) ? data.quickReplies : [],
          originId: data?.originId ?? null,
        });
      } catch (err) {
        if (isCancelled) return;
        console.error("Erro ao carregar sugestões de mensagens:", err);
        setSuggestionsError(err?.message || "Erro ao carregar sugestões de mensagens.");
      } finally {
        if (!isCancelled) {
          setIsLoadingSuggestions(false);
        }
      }
    }

    loadSuggestions();

    return () => {
      isCancelled = true;
    };
  }, [selectedConversation?.id, selectedConversation?.typeId, selectedConversation?.channelId, channelType, selectedChannel]);

  // Calcula se, de acordo com as regras de 24h do WhatsApp,
  // é recomendável iniciar a conversa usando template.
  const requiresTemplate = (() => {
    if (channelType !== "WhatsApp") return false;
    if (isWhatsAppQrChannelTipo(selectedChannelTipo)) return false;

    if (!Array.isArray(messages) || messages.length === 0) {
      // Conversa sem histórico: tipicamente é preciso usar template para iniciar.
      return true;
    }

    const clientMessages = messages.filter((msg) => {
      if (!msg) return false;
      const senderType = msg.senderType || msg.sender;
      if (!senderType) return false;
      const normalized = String(senderType).toLowerCase();
      return normalized === "client" || normalized === "cliente" || normalized === "user";
    });

    if (!clientMessages.length) return false;

    let lastClientDate = null;
    clientMessages.forEach((msg) => {
      const ts =
        msg.updatedAt ||
        msg.lastUpdate ||
        msg.timestamp ||
        msg.date;
      if (!ts) return;
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) return;
      if (!lastClientDate || d > lastClientDate) {
        lastClientDate = d;
      }
    });

    if (!lastClientDate) return false;

    const diffMs = Date.now() - lastClientDate.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours >= 24;
  })();

  const handleGenerateSuggestion = async () => {
    if (
      !selectedConversation?.id ||
      isGeneratingSuggestion ||
      !hasHistory
    )
      return;
    setIsGeneratingSuggestion(true);
    try {
      const extra = suggestionExtrasRef.current || {};
      const body = {
        draft: message?.trim() || "",
        channelName,
        channelType,
        channelId: selectedChannel,
      };
      if (extra.focusProductIds?.length) body.focusProductIds = extra.focusProductIds;
      if (extra.focusLineItemIds?.length) body.focusLineItemIds = extra.focusLineItemIds;
      const response = await generateSuggestedReply(selectedConversation.id, body);
      suggestionExtrasRef.current = { focusProductIds: [], focusLineItemIds: [] };
      const suggestion =
        response?.suggestion || response?.data?.suggestion || "";
      if (suggestion) {
        setMessage(suggestion);
      }
    } catch (error) {
      // manter simples, seguindo padrão atual
      // eslint-disable-next-line no-alert
      alert(
        "Não foi possível gerar uma sugestão de resposta agora. Tente novamente em instantes."
      );
      // eslint-disable-next-line no-console
      console.error("Erro ao gerar sugestão de resposta com IA:", error);
    } finally {
      setIsGeneratingSuggestion(false);
    }
  };

  const renderChannelComponent = () => {
    switch (channelType) {
      case "WhatsApp":
        return (
          <MessageWhatsAppChannel
            message={message}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            formatOptions={formatOptions}
            isRecording={isRecording}
            startRecording={startRecording}
            stopRecording={stopRecording}
            openFilePicker={openFilePicker}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
            suggestions={suggestions}
            isLoadingSuggestions={isLoadingSuggestions}
            suggestionsError={suggestionsError}
            requiresTemplate={requiresTemplate}
            selectedTemplate={selectedTemplate}
            onTemplateSelect={setSelectedTemplate}
          />
        );
      case "Email":
        return (
          <MessageEmailChannel
            message={message}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            formatOptions={formatOptions}
            isRecording={isRecording}
            startRecording={startRecording}
            stopRecording={stopRecording}
            openFilePicker={openFilePicker}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            isMobile={isMobile}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            handleRemoveFile={handleRemoveFile}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
            recipientEmail={selectedConversation?.email}
            messages={messages}
          />
        );
      case "Phone":
        return <MessagePhoneChannel
          selectedChannel={selectedChannel}
          conversationId={selectedConversation?.id}
          handleChannelChange={handleChannelChange}
          originOptions={["computer", "(11) 91234-5678", "custom"]}
          userPhone="(11) 91234-5678" // Exemplo do telefone do usuário
          customerPhones={["(21) 99876-5432", "(31) 98765-4321"]} // Telefones do cliente
          origin={origin}
          setOrigin={setOrigin}
          destination={destination}
          setDestination={setDestination}
          handleSendMessage={handleSendMessage}
          isMobile={isMobile}
        /> ;
      case "Messenger":
        return (
          <MessageMessengerChannel
            message={message}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            formatOptions={formatOptions}
            isRecording={isRecording}
            startRecording={startRecording}
            stopRecording={stopRecording}
            openFilePicker={openFilePicker}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
          />
        );
      case "SMS":
        return (
          <MessageSMSChannel
            message={message}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            formatOptions={formatOptions}
            isRecording={isRecording}
            startRecording={startRecording}
            stopRecording={stopRecording}
            openFilePicker={openFilePicker}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
          />
        );
      case "App":
        return (
          <MessageAppChannel
            message={message}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            formatOptions={formatOptions}
            isRecording={isRecording}
            startRecording={startRecording}
            stopRecording={stopRecording}
            openFilePicker={openFilePicker}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
          />
        );
      case "Wordpress":
        return (
          <MessageWordpressChannel
            message={message}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            formatOptions={formatOptions}
            isRecording={isRecording}
            startRecording={startRecording}
            stopRecording={stopRecording}
            openFilePicker={openFilePicker}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
          />
        );
      default:
        return (
          <MessageInputField
            message={message}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            formatOptions={formatOptions}
            isRecording={isRecording}
            startRecording={startRecording}
            stopRecording={stopRecording}
            openFilePicker={openFilePicker}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
          />
        );
    }
  };

  const handleChannelChange = (channelId) => {
    setSelectedChannel(channelId);
    sessionStorage.setItem(`selectedChannel${selectedConversation?.id}`, channelId);
  };

  const handleSendMessage = (emailOverrides = {}) => {
    const hasContent = message.trim() || files.length > 0 || audioURL;
    if (hasContent) {
      const payload = {
        message: message.trim(),
        files: files.length ? [...files] : [],
        audioURL: audioURL || null,
        channel: selectedChannel,
        channelType,
        ...(transferTo && { transferTo }),
        ...(selectedTemplate?.id && { templateId: selectedTemplate.id }),
        ...(channelType === "Email" && {
          emailTo: emailOverrides.emailTo ?? selectedConversation?.email,
          ...(emailOverrides.emailSubject != null && { emailSubject: emailOverrides.emailSubject }),
          ...(emailOverrides.emailCc != null && { emailCc: emailOverrides.emailCc }),
        }),
      };
      if (typeof onSendMessage === "function") {
        onSendMessage(payload);
      } else {
        console.error("onSendMessage não é uma função válida.");
      }
      setMessage("");
      setFiles([]);
      setAudioURL(null);
      setTransferTo(null);
      setSelectedTemplate(null);
    }
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null; // Resetar o input de arquivo.
      fileInputRef.current.click();
    }
  };

  const handleFileUploadInput = (e) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    if (selectedFiles.length) {
      handleFileUpload(selectedFiles);
    }
    e.target.value = "";
  };

  const handleFileUpload = (fileOrFiles) => {
    if (!fileOrFiles) return;
    const toAdd = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    setFiles((prev) => [...prev, ...toAdd]);
  };

  const handleRemoveFile = (index) => {
    if (typeof index === "number") {
      setFiles((prev) => prev.filter((_, i) => i !== index));
    } else {
      setFiles([]);
    }
  };

  // Ao remover o áudio, limpar também o anexo (files) para evitar que o preview de arquivo fique visível
  const handleRemoveAudio = () => {
    if (audioURL && audioURL.startsWith("blob:")) {
      URL.revokeObjectURL(audioURL);
    }
    setAudioURL(null);
    setFiles([]);
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices) {
      alert("Gravação de áudio não é suportada neste navegador.");
      return;
    }
    try {
      if (!mp3RecorderRef.current) {
        mp3RecorderRef.current = new MicRecorder({ bitRate: 128 });
      }
      await mp3RecorderRef.current.start();
      setIsRecording(true);
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((d) => d + 1);
      }, 1000);
    } catch (e) {
      console.error("Erro ao iniciar gravação:", e);
      alert("Não foi possível acessar o microfone. Verifique as permissões.");
    }
  };

  const stopRecording = () => {
    discardRequestedRef.current = false;
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setRecordingDuration(0);
    if (!mp3RecorderRef.current) {
      setIsRecording(false);
      return;
    }
    mp3RecorderRef.current
      .stop()
      .getMp3()
      .then(([buffer, blob]) => {
        if (discardRequestedRef.current) {
          discardRequestedRef.current = false;
          setIsRecording(false);
          return;
        }
        const audioUrl = URL.createObjectURL(blob);
        setAudioURL(audioUrl);
        setIsRecording(false);
      })
      .catch((e) => {
        console.error("Erro ao finalizar gravação:", e);
        discardRequestedRef.current = false;
        setIsRecording(false);
      });
  };

  const discardRecording = () => {
    discardRequestedRef.current = true;
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setRecordingDuration(0);
    if (!mp3RecorderRef.current) {
      setIsRecording(false);
      return;
    }
    mp3RecorderRef.current
      .stop()
      .getMp3()
      .then(([buffer, blob]) => {
        discardRequestedRef.current = false;
        setIsRecording(false);
      })
      .catch(() => {
        discardRequestedRef.current = false;
        setIsRecording(false);
      });
  };

  const handleEmojiPickerOpen = (event) => setEmojiAnchorEl(event.currentTarget);
  const handleEmojiPickerClose = () => setEmojiAnchorEl(null);
  const handleSelectEmoji = (emoji) => setMessage((prev) => `${prev}${emoji}`);

  return (
    <FileDropHandler onFileUpload={handleFileUpload}>
      {/* Respiro no rodapé (desktop também não pode ficar colado). */}
      <Box paddingBottom={isMobile ? 1 : 0.5}>
        <Box 
          display="flex"
          alignItems="center"
          gap={isMobile ? 0.5 : 1}
          flexWrap={isMobile ? "wrap" : "nowrap"} // Para evitar quebra de linha no desktop
          sx={{
            justifyContent: isMobile ? "space-between" : "flex-start", // Distribuição diferente entre mobile e desktop
          }}
        >
          {(!isMobile && !isExpanded) && <ChannelSelector selectedChannel={selectedChannel} setSelectedChannel={handleChannelChange} />}
          {/* {channelType === "Phone" ? (
            <MessagePhoneChannel
              selectedChannel={selectedChannel}
              handleChannelChange={handleChannelChange}
              originOptions={["computer", "(11) 91234-5678", "custom"]}
              userPhone="(11) 91234-5678" // Exemplo do telefone do usuário
              customerPhones={["(21) 99876-5432", "(31) 98765-4321"]} // Telefones do cliente
              origin={origin}
              setOrigin={setOrigin}
              destination={destination}
              setDestination={setDestination}
              handleSendMessage={handleSendMessage}
              isMobile={isMobile}
            />               
          ) : (<MessageInputField
              message={message}
              setMessage={setMessage}
              selectedChannel={selectedChannel}
              channelType={channelType}
              setSelectedChannel={handleChannelChange}
              formatOptions={formatOptions}
              isRecording={isRecording}
              startRecording={startRecording}
              stopRecording={stopRecording}
              openFilePicker={openFilePicker}
              handleEmojiPickerOpen={handleEmojiPickerOpen}
              handleSendMessage={handleSendMessage}
              file={files[0]}
              audioURL={audioURL}
          />)} */}
          <Box
            ref={mentionAnchorRef}
            sx={{
              flex: 1,
              minWidth: 0,
              width: "100%",
              minHeight: 48,
              display: "flex",
              alignItems: "stretch",
              transition: "opacity 0.2s ease",
            }}
          >
            {isRecording ? (
              <RecordingIndicator
                durationSeconds={recordingDuration}
                onStop={stopRecording}
                onDiscard={discardRecording}
              />
            ) : audioURL ? (
              <AudioPreview audioURL={audioURL} onRemove={handleRemoveAudio} />
            ) : (
              renderChannelComponent()
            )}
          </Box>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            multiple
            onChange={handleFileUploadInput}
          />
          {(!isMobile && channelType !== "Phone" && !isExpanded) && (
            <IconButton
              onClick={handleSendMessage}
              disabled={!message.trim() && files.length === 0 && !audioURL}
              sx={{
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                borderRadius: "50%",
                padding: "8px",
                "&:hover": { backgroundColor: "primary.dark" },
              }}
            >
              <Send />
            </IconButton>
          )}
        </Box>
        {!isExpanded && files.length > 0 && (
          <Box sx={{ width: "100%", mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {files.map((file, index) => (
              <FilePreview
                key={`${file.name}-${file.size}-${index}`}
                file={file}
                onRemove={() => handleRemoveFile(index)}
              />
            ))}
          </Box>
        )}
        <Popover
          open={Boolean(emojiAnchorEl)}
          anchorEl={emojiAnchorEl}
          onClose={handleEmojiPickerClose}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          slotProps={{
            paper: { sx: { maxHeight: "min(400px, 70vh)", overflow: "auto" } },
          }}
        >
          <EmojiPicker onSelectEmoji={handleSelectEmoji} />
        </Popover>
        {/* Lista de menções: overlay em Portal acima do input (sem roubar foco) */}
        {mentionOverlayPosition &&
          createPortal(
            <Box
              role="listbox"
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              sx={{
                position: "fixed",
                bottom: mentionOverlayPosition.bottom,
                left: mentionOverlayPosition.left,
                width: mentionOverlayPosition.width,
                maxHeight: "min(280px, 50vh)",
                overflow: "auto",
                bgcolor: "background.paper",
                boxShadow: 2,
                borderRadius: 1,
                zIndex: 1300,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {mentionOptions.map((opt, idx) => (
                <Box
                  key={`${opt.type}-${opt.id}-${idx}`}
                  component="div"
                  onClick={() => handleSelectMention(opt)}
                  onMouseDown={(e) => e.preventDefault()}
                  sx={{
                    px: 1.5,
                    py: 1,
                    cursor: "pointer",
                    bgcolor: idx === mentionHighlightIndex ? "action.selected" : "transparent",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Typography variant="body2" component="span">
                    {String(opt.label ?? "")}
                  </Typography>
                  {opt.kind ? (
                    <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 1 }}>
                      ({String(opt.kind)})
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Box>,
            document.body
          )}
      </Box>
    </FileDropHandler>
  );
};

export default MessageInput;
